import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import AppBrandMark from "../Branding/AppBrandMark";
import { sideMenuList } from "@Constant/constants";
import CustomArrowIcon from "../../assets/icons/SvgIcons/CustomArrowIcon";
import ChevronDownIcon from "../../assets/icons/SvgIcons/ChevronDownIcon";

const Sidebar = ({ onToggle, isSidebarCollapsed, isMobileTablet }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [hoverStart, setHoverStart] = useState(false);
  const sidebarRef = useRef(null);
  const state = location.state;
  const path = state?.path;
  const permissionsForRole = useSelector(
    (state) => state?.authSlice?.user?.user?.permissions,
  );

  const handleNavigation = (path) => {
    if (path) navigate(path);
  };

  const toggleSubMenu = (index) => {
    setExpandedMenus((prev) => ({
      [index]: !prev[index],
    }));
  };

  // Click outside to collapse (only on mobile/tablet)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobileTablet &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target)
      ) {
        onToggle(true);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileTablet]);

  const renderMenuItemContent = (item, isActive, isExpanded) => (
    <>
      <div className="flex items-center gap-2 w-full">
        <div
          className="max-w-[16px] h-[16px] shrink-0 [&_svg]:w-4 [&_svg]:h-4"
          dangerouslySetInnerHTML={{ __html: item.icon(isActive) }}
        />
        {isSidebarCollapsed && hoverStart && (
          <span
            className={`transition-all duration-300 select-none ${
              isActive ? "" : "text-white"
            }`}
          >
            {item.name}
          </span>
        )}
        {!isSidebarCollapsed && (
          <span
            className={`transition-all duration-300 select-none ${
              isActive ? "" : "text-white"
            }`}
          >
            {item.name}
          </span>
        )}
      </div>
      {item.subItems.length > 0 && (
        <ChevronDownIcon
          fill="white"
          className={`max-w-[10px] h-[6px] ${
            isSidebarCollapsed && !hoverStart ? "hidden" : "block"
          } ${isExpanded ? "rotate-180" : ""}`}
        />
      )}
    </>
  );

  return (
    <div
      ref={sidebarRef}
      onMouseEnter={() => !isMobileTablet && setHoverStart(true)}
      onMouseLeave={() => !isMobileTablet && setHoverStart(false)}
      className={`fixed lg:flex flex-col z-50 h-full bg-main ${
        isSidebarCollapsed
          ? isMobileTablet
            ? "w-0"
            : hoverStart
              ? "w-[232px]"
              : "w-0 lg:w-[72px]"
          : "w-[232px]"
      } transition-all duration-300 ${!isSidebarCollapsed ? "" : "menu-close"}`}
    >
      {/* Header */}
      <div
        className={`flex items-center border-b-[0.5px] border-[#14508F1A] h-[56px] relative bg-white overflow-visible ${
          isMobileTablet && isSidebarCollapsed
            ? "p-0"
            : !isSidebarCollapsed || hoverStart
              ? "py-1.5 pl-4 pr-10"
              : "py-1.5 px-2 justify-center"
        }`}
      >
        <div
          className={`flex items-center w-full min-w-0 ${
            !isSidebarCollapsed || hoverStart ? "justify-start" : "justify-center"
          }`}
        >
          {!isSidebarCollapsed || hoverStart ? (
            <AppBrandMark variant="sidebar" />
          ) : (
            <AppBrandMark variant="sidebar-collapsed" />
          )}

          <button
            type="button"
            onClick={() => onToggle(!isSidebarCollapsed)}
            className="cursor-pointer absolute top-1/2 -translate-y-1/2 right-[-10px] z-[60]"
            aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={isSidebarCollapsed ? "Expand" : "Collapse"}
          >
            <CustomArrowIcon />
          </button>
        </div>
      </div>

      {/* Menu */}
      <div
        className={`${
          isMobileTablet && isSidebarCollapsed ? "p-0" : "py-2 px-1.5"
        } overflow-y-auto overflow-x-hidden h-[calc(100%_-_56px)]`}
      >
        <ul>
          {sideMenuList.map((item, index) => {
            const isSubItems = item?.subItems?.length > 0;
            const isActive =
              (location.pathname.startsWith(item?.path) ||
                path?.replace(/^\//, "") === item?.path?.replace(/^\//, "")) &&
              !isSubItems;
            const isExpanded = expandedMenus[index];

            // const hasPermission = true;
            const hasPermission = item?.permissions?.some((permission) =>
              permissionsForRole?.includes(permission),
            );

            if (!hasPermission) return null;

            const wrapperProps =
              item.subItems.length === 0
                ? {
                    to: item.path,
                    onClick: () => {
                      if (isMobileTablet) onToggle(true);
                    },
                  }
                : {
                    onClick: () => {
                      handleNavigation(item.path);
                      toggleSubMenu(index);
                    },
                  };

            const Wrapper = item.subItems.length === 0 ? Link : "a";

            return (
              <li key={index} className="cursor-pointer">
                <Wrapper
                  {...wrapperProps}
                  className={`flex items-center w-full rounded-[5px] px-2.5 py-2 mb-1 text-[13px] font-medium ${
                    isActive
                      ? "bg-white text-main"
                      : "bg-transparent text-white"
                  }`}
                >
                  {renderMenuItemContent(item, isActive, isExpanded)}
                </Wrapper>

                {/* Sub-Menu Items */}
                {isSubItems && isExpanded && (
                  <ul>
                    {item.subItems.map(
                      (subitem, subIndex) =>
                        permissionsForRole?.includes(subitem?.permission) && (
                          <li key={subIndex}>
                            <Link
                              to={subitem.path}
                              onClick={() => {
                                if (isMobileTablet) onToggle(true);
                              }}
                              className={`mb-1 flex items-start w-full rounded-[5px] font-medium text-[12px] pl-7 text-left pr-2.5 py-1.5 leading-5 cursor-pointer select-none ${
                                location.pathname.startsWith(subitem.path)
                                  ? "bg-white text-main"
                                  : "bg-transparent !text-white"
                              }`}
                            >
                              {isSidebarCollapsed && hoverStart && (
                                <>
                                  <span className="mr-[18px]">-</span>
                                  {subitem.name}
                                </>
                              )}
                              {!isSidebarCollapsed && (
                                <>
                                  <span className="mr-[18px]">-</span>
                                  {subitem.name}
                                </>
                              )}
                              {isSidebarCollapsed && !hoverStart && (
                                <span>-</span>
                              )}
                            </Link>
                          </li>
                        ),
                    )}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
